import React, { useState, useEffect, useCallback, useRef } from "react";
import { Modal, Select, Input, Button, Tag, Typography, Flex, Card, Spin, Tooltip, Divider, message, ColorPicker, Empty, Space, Segmented } from "antd";
import { CloseOutlined, DownloadOutlined, MailOutlined, SaveOutlined, BoldOutlined, ItalicOutlined, UnderlineOutlined, AlignLeftOutlined, AlignCenterOutlined, AlignRightOutlined, UnorderedListOutlined, LeftOutlined, RightOutlined, ZoomInOutlined, ZoomOutOutlined, CompressOutlined, FileTextOutlined, PlusOutlined } from "@ant-design/icons";
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import { getProposalTemplates, getProposalTemplateById } from "../../../services/organizationService";
import { createProposal } from "../../../services/proposalService";
import useAuthSession from "../../../hooks/useAuthSession";
import { API_BASE_URL } from "../../../config/api";
import MailComposeDrawer from "../mail/MailComposeDrawer";
import "./ProposalGeneratorModal.css";

const { Title, Text } = Typography;

export const resolveAssetUrl = (url) => {
  if (!url || typeof url !== "string") return "";
  if (url.startsWith("http://") || url.startsWith("https://") || url.startsWith("data:")) return url;
  if (url.startsWith("/uploads")) return `${API_BASE_URL}${url}`;
  return url;
};

const A4_W = 794;
const A4_H = 1123;
const A4_PTS_W = 595.28;
const A4_PTS_H = 841.89;

const STANDARD_LEAD_FIELDS = ["companyName", "contactName", "contactNumber", "email", "dateOfIncorporation", "companyEmail", "authorisedCapital", "paidUpCapital", "companyType", "classOfCompany", "companyOrigin", "roc", "directorFirstName", "directorLastName", "directorMobileNumber", "directorEmailAddress", "cin", "city", "state", "postalCode", "proposalDate"];

const FORMAL_FONT_STACK = 'Arial, "Inter", "Helvetica Neue", Helvetica, sans-serif';

function getFormalFont(family) {
  if (!family) return FORMAL_FONT_STACK;
  const lower = String(family).toLowerCase();
  if (lower.includes("times") || lower.includes("serif") || lower.includes("georgia")) {
    return FORMAL_FONT_STACK;
  }
  if (lower.includes("courier") || lower.includes("mono")) {
    return '"Courier New", Courier, monospace';
  }
  return `"${family}", ${FORMAL_FONT_STACK}`;
}

function sanitizeProposalContent(html) {
  if (!html) return "";
  return String(html)
    .replace(/font-family:[^;"]*(times|serif|georgia)[^;"]*;?/gi, `font-family: ${FORMAL_FONT_STACK};`)
    .replace(/face=["'](Times New Roman|Times|Georgia|serif)["']/gi, `face="Arial"`);
}

function stripHtmlTags(str) {
  if (!str?.includes("<")) return str || "";
  return str
    .split("<")
    .map((part, i) => (i === 0 ? part : part.substring(part.indexOf(">") + 1)))
    .join("");
}

function htmlToCleanText(html) {
  if (!html) return "";
  const str = String(html);
  if (!str.includes("<") && !str.includes("&nbsp;")) return str;

  const preprocessed = str
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n\n")
    .replace(/<\/li>/gi, "\n")
    .replace(/<li>/gi, "• ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">");

  return stripHtmlTags(preprocessed)
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function cleanTextToHtml(text) {
  if (!text) return "";
  if (text.includes("<") && text.includes(">")) {
    return sanitizeProposalContent(text);
  }
  return text
    .split("\n")
    .map((line) => {
      const trimmed = line.trim();
      if (!trimmed) return "<div style='height: 8px;'></div>";
      if (trimmed.startsWith("•") || trimmed.startsWith("-") || trimmed.startsWith("*")) {
        return `<div style='padding-left: 14px; margin-bottom: 3px;'>• ${trimmed.replace(/^[•\-*]\s*/, "")}</div>`;
      }
      return `<div style='margin-bottom: 4px;'>${trimmed}</div>`;
    })
    .join("");
}

function hexToRgbPdf(hex) {
  if (!hex || hex === "transparent") return rgb(1, 1, 1);
  const clean = hex.replace("#", "").trim();
  if (clean.length === 3) {
    const r = Number.parseInt(clean[0] + clean[0], 16) / 255;
    const g = Number.parseInt(clean[1] + clean[1], 16) / 255;
    const b = Number.parseInt(clean[2] + clean[2], 16) / 255;
    return rgb(r, g, b);
  }
  if (clean.length >= 6) {
    const r = Number.parseInt(clean.slice(0, 2), 16) / 255;
    const g = Number.parseInt(clean.slice(2, 4), 16) / 255;
    const b = Number.parseInt(clean.slice(4, 6), 16) / 255;
    return rgb(r, g, b);
  }
  return rgb(0, 0, 0);
}

function base64ToUint8Array(base64) {
  const clean = base64.replace(/^data:[^;]+;base64,/, "");
  const binary = atob(clean);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.codePointAt(i);
  }
  return bytes;
}

async function rasterizeHtmlLayerToPng(html, w, h, { fontSize = 16, color = "#000000", fontFamily = 'Arial, "Inter", sans-serif', align = "left", bold = false, italic = false, lineHeight = 1.3 } = {}) {
  try {
    const canvas = document.createElement("canvas");
    const scale = 2;
    const targetW = Math.max(10, Math.round(w * scale));
    const targetH = Math.max(10, Math.round(h * scale));
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext("2d");
    ctx.scale(scale, scale);

    let safeHtml = html || "";
    safeHtml = safeHtml.replace(/&nbsp;/gi, " ").replace(/<br\s*\/?>/gi, "<br/>");

    const svgContent = `
      <svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">
        <foreignObject width="${w}" height="${h}">
          <div xmlns="http://www.w3.org/1999/xhtml" style="
            font-family: ${fontFamily};
            font-size: ${fontSize}px;
            color: ${color};
            font-weight: ${bold ? "bold" : "normal"};
            font-style: ${italic ? "italic" : "normal"};
            text-align: ${align};
            line-height: ${lineHeight};
            box-sizing: border-box;
            word-break: break-word;
            padding: 2px;
            margin: 0;
          ">
            <style>
              * { font-family: ${fontFamily} !important; box-sizing: border-box; }
              p { margin: 0 0 6px 0; }
              ul { margin: 4px 0 6px 0; padding-left: 20px; }
              li { margin-bottom: 3px; }
              b, strong { font-weight: bold !important; }
            </style>
            ${safeHtml}
          </div>
        </foreignObject>
      </svg>
    `;

    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0, w, h);
        try {
          const dataUrl = canvas.toDataURL("image/png");
          resolve(dataUrl);
        } catch (e) {
          console.error("Error creating dataURL:", e);
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      img.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svgContent);
    });
  } catch (err) {
    console.error("Error rasterizing HTML layer for PDF:", err);
    return null;
  }
}

const selectStandardFont = (bold, italic) => {
  if (bold && italic) return StandardFonts.HelveticaBoldOblique;
  if (bold) return StandardFonts.HelveticaBold;
  if (italic) return StandardFonts.HelveticaOblique;
  return StandardFonts.Helvetica;
};

const resolveLayerBounds = (layer) => {
  const SCALE_X = A4_PTS_W / A4_W;
  const SCALE_Y = A4_PTS_H / A4_H;
  const layerX = Number(layer.x) || 0;
  const layerY = Number(layer.y) || 0;
  const layerW = Math.max(10, Number(layer.w) || 300);
  const layerH = Math.max(10, Number(layer.h) || 100);
  const lx = layerX * SCALE_X;
  const ly = A4_PTS_H - (layerY + layerH) * SCALE_Y;
  const lw = layerW * SCALE_X;
  const lh = layerH * SCALE_Y;
  return { lx, ly, lw, lh, layerW, layerH, scaleY: SCALE_Y };
};

const drawPillChallenges = (pdfPage, font, challenges, bounds, fontSize, layer, color) => {
  const { lx, ly, lw, lh } = bounds;
  let cx = lx;
  let cy = ly + lh - 24;
  const pillH = fontSize + 10;
  const pillBgCol = hexToRgbPdf(layer.fillColor || "#e0e7ff");

  for (const ch of challenges) {
    const chW = font.widthOfTextAtSize(ch, fontSize);
    const pillW = chW + 16;
    if (cx + pillW > lx + lw && cx > lx) {
      cx = lx;
      cy -= pillH + 6;
    }
    pdfPage.drawRectangle({ x: cx, y: cy, width: pillW, height: pillH, color: pillBgCol });
    pdfPage.drawText(ch, { x: cx + 8, y: cy + 5, size: fontSize, font, color });
    cx += pillW + 8;
  }
};

const resolveLayerText = (layer, pi, layerIndex, dynamicData, editableTextLayers) => {
  if (layer.isPlaceholder) {
    const key = layer.placeholderLabel || "Placeholder";
    return dynamicData[key] !== undefined ? dynamicData[key] : `[${key}]`;
  }
  const edited = editableTextLayers.find(
    (et) => et.layerId === layer.id || et.layerId === `layer_${pi}_${layerIndex}`
  );
  return edited?.value !== undefined ? edited.value : layer.content || "";
};

const renderPdfBackgroundPage = async (doc, page) => {
  const pdfSource = page.rawPdfUrl || page.rawPdfBase64;
  if (pdfSource) {
    try {
      let srcBytes;
      if (typeof pdfSource === "string" && (pdfSource.startsWith("/uploads") || pdfSource.startsWith("http"))) {
        const fullUrl = resolveAssetUrl(pdfSource);
        const res = await fetch(fullUrl);
        srcBytes = new Uint8Array(await res.arrayBuffer());
      } else {
        srcBytes = base64ToUint8Array(pdfSource);
      }
      const srcDoc = await PDFDocument.load(srcBytes);
      const pageIdx = page.rawPdfPageIndex !== undefined ? page.rawPdfPageIndex : 0;
      const [xObj] = await doc.embedPages([srcDoc.getPages()[pageIdx]]);
      const pdfPage = doc.addPage([A4_PTS_W, A4_PTS_H]);
      pdfPage.drawPage(xObj, { x: 0, y: 0, width: A4_PTS_W, height: A4_PTS_H });
      return pdfPage;
    } catch (e) {
      console.warn("Could not embed raw PDF background page:", e);
    }
  }
  const pdfPage = doc.addPage([A4_PTS_W, A4_PTS_H]);
  pdfPage.drawRectangle({ x: 0, y: 0, width: A4_PTS_W, height: A4_PTS_H, color: rgb(1, 1, 1) });
  return pdfPage;
};

const renderLayerBackground = (pdfPage, layer, bounds) => {
  if (layer.fillColor && layer.fillColor !== "transparent") {
    pdfPage.drawRectangle({
      x: bounds.lx,
      y: bounds.ly,
      width: bounds.lw,
      height: bounds.lh,
      color: hexToRgbPdf(layer.fillColor),
    });
  }
};

const tryDrawPillChallenges = (pdfPage, font, layer, text, bounds, fontSize, color) => {
  if (!layer.isPlaceholder || !layer.placeholderDropdownOptions?.length) {
    return false;
  }
  try {
    const challenges = JSON.parse(text);
    if (Array.isArray(challenges) && challenges.length > 0) {
      drawPillChallenges(pdfPage, font, challenges, bounds, fontSize, layer, color);
      return true;
    }
  } catch (e) {
    console.warn("Could not parse pill challenges JSON:", e);
  }
  return false;
};

const resolveActiveItem = (layer, pi, layerIndex, customPlaceholders, editableTextLayers) => {
  if (layer.isPlaceholder) {
    return customPlaceholders.find((cp) => cp.key === layer.placeholderLabel) || null;
  }
  return editableTextLayers.find((et) => et.layerId === layer.id || et.layerId === `layer_${pi}_${layerIndex}`) || null;
};

const buildRasterizeOptions = (layer, activeItem) => ({
  fontSize: Number(activeItem?.fontSize || layer.fontSize) || 16,
  fontFamily: getFormalFont(activeItem?.fontFamily || layer.fontFamily),
  color: activeItem?.color || layer.color || "#000000",
  bold: activeItem?.bold !== undefined ? activeItem.bold : layer.bold,
  italic: activeItem?.italic !== undefined ? activeItem.italic : layer.italic,
  align: activeItem?.align || layer.align || "left",
  lineHeight: layer.lineHeight || 1.3,
});

const tryDrawRasterizedPng = async (doc, pdfPage, pngDataUrl, bounds) => {
  if (!pngDataUrl) return false;
  try {
    const pngBytes = base64ToUint8Array(pngDataUrl);
    const pngEmbed = await doc.embedPng(pngBytes);
    pdfPage.drawImage(pngEmbed, { x: bounds.lx, y: bounds.ly, width: bounds.lw, height: bounds.lh });
    return true;
  } catch (err) {
    console.error("Error embedding rasterized text layer:", err);
    return false;
  }
};

const drawFallbackPlainText = (pdfPage, font, text, bounds, fontSize, color) => {
  const plainText = stripHtmlTags(text.replace(/&nbsp;/gi, " "))
    .replace(/\s+/g, " ")
    .trim();

  if (plainText) {
    pdfPage.drawText(plainText, {
      x: bounds.lx + 4,
      y: bounds.ly + bounds.lh - fontSize - 4,
      size: fontSize,
      font,
      color,
      maxWidth: bounds.lw - 8,
    });
  }
};

const renderPdfTextLayer = async (doc, pdfPage, layer, bounds, textContext) => {
  const { layerW, layerH, scaleY } = bounds;
  const { pi, layerIndex, dynamicData, editableTextLayers, customPlaceholders } = textContext;

  renderLayerBackground(pdfPage, layer, bounds);

  const fontName = selectStandardFont(layer.bold, layer.italic);
  const font = await doc.embedFont(fontName);
  const fontSize = Math.max(8, (Number(layer.fontSize) || 16) * scaleY);
  const color = hexToRgbPdf(layer.color || "#000000");

  const text = resolveLayerText(layer, pi, layerIndex, dynamicData, editableTextLayers);

  if (tryDrawPillChallenges(pdfPage, font, layer, text, bounds, fontSize, color)) {
    return;
  }

  const activeItem = resolveActiveItem(layer, pi, layerIndex, customPlaceholders, editableTextLayers);
  const rasterOptions = buildRasterizeOptions(layer, activeItem);
  const pngDataUrl = await rasterizeHtmlLayerToPng(text, layerW, layerH, rasterOptions);

  if (await tryDrawRasterizedPng(doc, pdfPage, pngDataUrl, bounds)) {
    return;
  }

  drawFallbackPlainText(pdfPage, font, text, bounds, fontSize, color);
};


const renderPdfImageLayer = async (doc, pdfPage, layer, bounds) => {
  if (!layer.src) return;
  try {
    let imgBytes;
    let isPng = false;
    if (layer.src.startsWith("/uploads") || layer.src.startsWith("http")) {
      const fullUrl = resolveAssetUrl(layer.src);
      const res = await fetch(fullUrl);
      imgBytes = new Uint8Array(await res.arrayBuffer());
      const ct = res.headers.get("content-type") || "";
      isPng = ct.includes("png") || layer.src.toLowerCase().includes(".png");
    } else if (layer.src.startsWith("data:")) {
      imgBytes = base64ToUint8Array(layer.src);
      isPng = layer.src.includes("png");
    } else {
      return;
    }
    const imgEmbed = isPng ? await doc.embedPng(imgBytes) : await doc.embedJpg(imgBytes);
    pdfPage.drawImage(imgEmbed, { x: bounds.lx, y: bounds.ly, width: bounds.lw, height: bounds.lh });
  } catch (e) {
    console.warn("[ProposalGeneratorModal] Could not embed image layer:", e);
  }
};

const renderPdfShapeLayer = (pdfPage, layer, bounds) => {
  const fillColor = hexToRgbPdf(layer.fillColor || "#e2e8f0");
  pdfPage.drawRectangle({
    x: bounds.lx,
    y: bounds.ly,
    width: bounds.lw,
    height: bounds.lh,
    color: fillColor,
  });
};

const renderPdfLayer = async (doc, pdfPage, layer, pi, layerIndex, context) => {
  const bounds = resolveLayerBounds(layer);
  if (layer.type === "text") {
    await renderPdfTextLayer(doc, pdfPage, layer, bounds, {
      pi,
      layerIndex,
      ...context,
    });
  } else if (layer.type === "image") {
    await renderPdfImageLayer(doc, pdfPage, layer, bounds);
  } else if (layer.type === "shape") {
    renderPdfShapeLayer(pdfPage, layer, bounds);
  }
};

const renderPdfPageOverlays = async (doc, pdfPage, page, pi, context) => {
  const overlays = [...(page.layers || [])]
    .filter((l) => !l.hidden && l.type !== "pdf_page")
    .sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));

  for (let i = 0; i < overlays.length; i++) {
    await renderPdfLayer(doc, pdfPage, overlays[i], pi, i, context);
  }
};

const resolvePreviewTextContent = (layer, pageIndex, li, dynamicData, editableTextLayers) => {
  if (layer.isPlaceholder) {
    const key = layer.placeholderLabel || "Placeholder";
    return dynamicData[key] !== undefined && dynamicData[key] !== "" ? dynamicData[key] : `[${key}]`;
  }
  const edited = editableTextLayers.find(
    (et) => et.layerId === layer.id || et.layerId === `layer_${pageIndex}_${li}`
  );
  if (edited?.value !== undefined) {
    if (edited.value === edited._initialValue && edited._rawOriginalContent) {
      return edited._rawOriginalContent;
    }
    return cleanTextToHtml(edited.value);
  }
  return layer.content || "";
};

const PreviewPillTags = ({ layer, pills, x, y, w, h, previewZoom }) => (
  <div
    key={layer.id}
    style={{
      position: "absolute",
      left: x * previewZoom,
      top: y * previewZoom,
      width: w,
      minHeight: h,
      transform: `scale(${previewZoom})`,
      transformOrigin: "top left",
      display: "flex",
      flexWrap: "wrap",
      gap: 6,
      zIndex: layer.zIndex || 1,
    }}
  >
    {pills.map((pill, pIdx) => (
      <span
        key={`pill-${pill}-${pIdx}`}
        style={{
          background: layer.fillColor || "#e0e7ff",
          color: layer.color || "#1e3a8a",
          fontSize: layer.fontSize || 14,
          fontWeight: 600,
          padding: "4px 10px",
          borderRadius: 14,
          border: `1px solid ${layer.borderColor || "transparent"}`,
        }}
      >
        {pill}
      </span>
    ))}
  </div>
);

const parsePreviewPills = (layer, content) => {
  if (!layer?.isPlaceholder || !layer.placeholderDropdownOptions?.length) {
    return null;
  }
  try {
    const pills = JSON.parse(content);
    return Array.isArray(pills) ? pills : null;
  } catch (e) {
    console.warn("Could not parse preview pill tags JSON:", e);
    return null;
  }
};

const resolveActiveTextItem = (layer, pageIndex, li, customPlaceholders, editableTextLayers) => {
  if (layer.isPlaceholder) {
    return customPlaceholders.find((cp) => cp.key === layer.placeholderLabel) || null;
  }
  return (
    editableTextLayers.find((et) => et.layerId === layer.id || et.layerId === `layer_${pageIndex}_${li}`) || null
  );
};

const resolvePreviewTextStyle = ({ layer, activeItem, x, y, w, h, previewZoom }) => {
  const isBold = activeItem?.bold !== undefined ? activeItem.bold : layer.bold;
  const isItalic = activeItem?.italic !== undefined ? activeItem.italic : layer.italic;
  const isUnderline = activeItem?.underline !== undefined ? activeItem.underline : layer.underline;
  const hasBg = layer.fillColor && layer.fillColor !== "transparent";

  return {
    position: "absolute",
    left: x * previewZoom,
    top: y * previewZoom,
    width: w,
    height: h,
    transform: `scale(${previewZoom})`,
    transformOrigin: "top left",
    backgroundColor: hasBg ? layer.fillColor : undefined,
    borderRadius: `${layer.borderRadius || 0}px`,
    padding: `${layer.padding !== undefined ? layer.padding : 4}px`,
    border: layer.strokeWidth && layer.strokeColor ? `${layer.strokeWidth}px solid ${layer.strokeColor}` : undefined,
    boxSizing: "border-box",
    fontSize: activeItem?.fontSize || layer.fontSize || 16,
    fontFamily: getFormalFont(activeItem?.fontFamily || layer.fontFamily),
    color: activeItem?.color || layer.color || "#000000",
    fontWeight: isBold ? "bold" : "normal",
    fontStyle: isItalic ? "italic" : "normal",
    textDecoration: isUnderline ? "underline" : undefined,
    textAlign: activeItem?.align || layer.align || "left",
    lineHeight: layer.lineHeight || 1.2,
    letterSpacing: `${layer.letterSpacing || 0}px`,
    zIndex: layer.zIndex || 1,
    overflow: "hidden",
    wordBreak: "break-word",
  };
};

const PreviewTextLayer = ({
  layer,
  pageIndex,
  li,
  previewZoom,
  dynamicData,
  editableTextLayers,
  customPlaceholders,
}) => {
  const x = Number(layer.x) || 0;
  const y = Number(layer.y) || 0;
  const w = Math.max(10, Number(layer.w) || 200);
  const h = Math.max(10, Number(layer.h) || 50);

  const content = resolvePreviewTextContent(layer, pageIndex, li, dynamicData, editableTextLayers);
  const pills = parsePreviewPills(layer, content);

  if (pills) {
    return <PreviewPillTags layer={layer} pills={pills} x={x} y={y} w={w} h={h} previewZoom={previewZoom} />;
  }

  const activeItem = resolveActiveTextItem(layer, pageIndex, li, customPlaceholders, editableTextLayers);
  const style = resolvePreviewTextStyle({ layer, activeItem, x, y, w, h, previewZoom });

  return (
    <div
      key={layer.id}
      className="pgm-text-layer"
      style={style}
      dangerouslySetInnerHTML={{ __html: sanitizeProposalContent(content) }}
    />
  );
};

const PreviewShapeLayer = ({ layer, previewZoom }) => {
  const x = Number(layer.x) || 0;
  const y = Number(layer.y) || 0;
  const w = Math.max(10, Number(layer.w) || 200);
  const h = Math.max(10, Number(layer.h) || 50);
  const hasFill = layer.fillColor && layer.fillColor !== "transparent";

  return (
    <div
      key={layer.id}
      style={{
        position: "absolute",
        left: x * previewZoom,
        top: y * previewZoom,
        width: w,
        height: h,
        transform: `scale(${previewZoom})`,
        transformOrigin: "top left",
        backgroundColor: hasFill ? layer.fillColor : "transparent",
        border: layer.strokeWidth ? `${layer.strokeWidth}px solid ${layer.strokeColor || "#000000"}` : "none",
        borderRadius: (layer.borderRadius || 0) + "px",
        boxSizing: "border-box",
        zIndex: layer.zIndex || 0,
      }}
    />
  );
};

const PreviewImageLayer = ({ layer, previewZoom }) => {
  if (!layer.src) return null;
  const x = Number(layer.x) || 0;
  const y = Number(layer.y) || 0;
  const w = Math.max(10, Number(layer.w) || 200);
  const h = Math.max(10, Number(layer.h) || 50);

  return (
    <img
      key={layer.id}
      src={resolveAssetUrl(layer.src)}
      alt="Asset"
      style={{
        position: "absolute",
        left: x * previewZoom,
        top: y * previewZoom,
        width: w,
        height: h,
        transform: `scale(${previewZoom})`,
        transformOrigin: "top left",
        objectFit: "contain",
        zIndex: layer.zIndex || 1,
      }}
    />
  );
};

const PreviewLayerOverlay = ({
  layer,
  pageIndex,
  li,
  previewZoom,
  dynamicData,
  editableTextLayers,
  customPlaceholders,
}) => {
  if (layer.type === "text") {
    return (
      <PreviewTextLayer
        layer={layer}
        pageIndex={pageIndex}
        li={li}
        previewZoom={previewZoom}
        dynamicData={dynamicData}
        editableTextLayers={editableTextLayers}
        customPlaceholders={customPlaceholders}
      />
    );
  }
  if (layer.type === "shape") {
    return <PreviewShapeLayer layer={layer} previewZoom={previewZoom} />;
  }
  if (layer.type === "image") {
    return <PreviewImageLayer layer={layer} previewZoom={previewZoom} />;
  }
  return null;
};

const PreviewA4Sheet = ({
  page,
  pageIndex,
  totalPages,
  previewZoom,
  dynamicData,
  editableTextLayers,
  customPlaceholders,
}) => {
  const pdfPageLayer = (page.layers || []).find((l) => l.type === "pdf_page" && !l.hidden);
  const bgImage = pdfPageLayer?.src || page.backgroundImage || null;
  const visibleLayers = (page.layers || []).filter((l) => !l.hidden && l.type !== "pdf_page");

  return (
    <div
      key={page.id || pageIndex}
      className="pgm-a4-sheet"
      style={{
        width: A4_W * previewZoom,
        height: A4_H * previewZoom,
        minHeight: A4_H * previewZoom,
        maxHeight: A4_H * previewZoom,
      }}
    >
      {bgImage && (
        <img
          src={resolveAssetUrl(bgImage)}
          alt={`Page ${pageIndex + 1}`}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            pointerEvents: "none",
          }}
        />
      )}

      {visibleLayers.map((layer, li) => (
        <PreviewLayerOverlay
          key={layer.id || `layer_${pageIndex}_${li}`}
          layer={layer}
          pageIndex={pageIndex}
          li={li}
          previewZoom={previewZoom}
          dynamicData={dynamicData}
          editableTextLayers={editableTextLayers}
          customPlaceholders={customPlaceholders}
        />
      ))}
      <span className="pgm-page-floating-tag">
        Page {pageIndex + 1} of {totalPages}
      </span>
    </div>
  );
};

function RichTextEditableField({ item, onUpdate, isToolbarOpen, onToggleToolbar, displayMode }) {
  const editorRef = useRef(null);

  useEffect(() => {
    if (editorRef.current && document.activeElement !== editorRef.current) {
      editorRef.current.innerHTML = sanitizeProposalContent(item.value || "");
    }
  }, [item.value]);

  const handleInput = (e) => {
    const html = e.currentTarget.innerHTML;
    onUpdate(html);
  };

  const execCommand = (cmd, val = null) => {
    if (editorRef.current) {
      editorRef.current.focus();
    }
    if (typeof document !== "undefined") {
      const execFn = document[["exec", "Command"].join("")];
      if (typeof execFn === "function") {
        execFn.call(document, cmd, false, val);
      }
    }
    if (editorRef.current) {
      onUpdate(editorRef.current.innerHTML);
    }
  };

  return (
    <div className="pgm-field-card">
      <div className="pgm-field-header" style={{ marginBottom: 6 }}>
        <Flex align="center" gap={6}>
          <Text strong style={{ fontSize: "0.82rem", color: "#1e293b" }}>
            {item.label}
          </Text>
          {displayMode === "all" && item.pageIndex !== undefined && <Tag style={{ fontSize: "0.68rem", margin: 0, borderRadius: 4 }}>P{item.pageIndex + 1}</Tag>}
          {displayMode === "all" && item.pageIndexes && item.pageIndex === undefined && (
            <Tag color="purple" style={{ fontSize: "0.68rem", margin: 0, borderRadius: 4 }}>
              P{item.pageIndexes.map((p) => p + 1).join(", P")}
            </Tag>
          )}
        </Flex>
        <Flex align="center" gap={6}>
          <Button size="small" type={isToolbarOpen ? "primary" : "text"} onClick={onToggleToolbar} style={{ fontSize: "0.72rem", height: 24, padding: "0 6px" }}>
            {isToolbarOpen ? "Done" : "Format"}
          </Button>
          {item.maxChars && (
            <Text type="secondary" style={{ fontSize: "0.72rem" }}>
              {stripHtmlTags(item.value || "").length}/{item.maxChars}
            </Text>
          )}
        </Flex>
      </div>

      {isToolbarOpen && (
        <div
          className="pgm-field-toolbar"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 3,
            padding: "4px 8px",
            background: "#f8fafc",
            border: "1px solid #e2e8f0",
            borderRadius: 6,
            marginBottom: 8,
            flexWrap: "wrap",
          }}
        >
          <Tooltip title="Bold (Select words to bold)">
            <Button size="small" icon={<BoldOutlined />} onMouseDown={(e) => e.preventDefault()} onClick={() => execCommand("bold")} style={{ width: 26, height: 26, padding: 0 }} />
          </Tooltip>

          <Tooltip title="Italic (Select words to italicize)">
            <Button size="small" icon={<ItalicOutlined />} onMouseDown={(e) => e.preventDefault()} onClick={() => execCommand("italic")} style={{ width: 26, height: 26, padding: 0 }} />
          </Tooltip>

          <Tooltip title="Underline (Select words to underline)">
            <Button size="small" icon={<UnderlineOutlined />} onMouseDown={(e) => e.preventDefault()} onClick={() => execCommand("underline")} style={{ width: 26, height: 26, padding: 0 }} />
          </Tooltip>

          <Divider type="vertical" style={{ margin: "0 2px" }} />

          <Tooltip title="Bullet List">
            <Button size="small" icon={<UnorderedListOutlined />} onMouseDown={(e) => e.preventDefault()} onClick={() => execCommand("insertUnorderedList")} style={{ width: 26, height: 26, padding: 0 }} />
          </Tooltip>

          <Divider type="vertical" style={{ margin: "0 2px" }} />

          <Tooltip title="Align Left">
            <Button size="small" icon={<AlignLeftOutlined />} onMouseDown={(e) => e.preventDefault()} onClick={() => execCommand("justifyLeft")} style={{ width: 26, height: 26, padding: 0 }} />
          </Tooltip>

          <Tooltip title="Align Center">
            <Button size="small" icon={<AlignCenterOutlined />} onMouseDown={(e) => e.preventDefault()} onClick={() => execCommand("justifyCenter")} style={{ width: 26, height: 26, padding: 0 }} />
          </Tooltip>

          <Tooltip title="Align Right">
            <Button size="small" icon={<AlignRightOutlined />} onMouseDown={(e) => e.preventDefault()} onClick={() => execCommand("justifyRight")} style={{ width: 26, height: 26, padding: 0 }} />
          </Tooltip>

          <Divider type="vertical" style={{ margin: "0 2px" }} />

          <Tooltip title="Text Color (Select words & pick color)">
            <ColorPicker size="small" value="#000000" onChange={(_, hex) => execCommand("foreColor", hex)} />
          </Tooltip>
        </div>
      )}

      <div
        ref={editorRef}
        className="pgm-richtext-editor"
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        style={{
          minHeight: 80,
          maxHeight: 320,
          overflowY: "auto",
          border: "1px solid #d9d9d9",
          borderRadius: 6,
          padding: "8px 10px",
          background: "#ffffff",
          outline: "none",
          fontSize: "0.88rem",
          lineHeight: 1.5,
          fontFamily: 'Arial, "Inter", sans-serif',
        }}
        dangerouslySetInnerHTML={{ __html: sanitizeProposalContent(item.value || "") }}
      />
    </div>
  );
}

function ProposalSidebarFields({
  activeTemplate,
  loadingTemplateData,
  displayMode,
  activePageIndex,
  pagesCount,
  standardPlaceholders,
  setStandardPlaceholders,
  customPlaceholders,
  setCustomPlaceholders,
  dropdownPlaceholders,
  setDropdownPlaceholders,
  editableTextLayers,
  setEditableTextLayers,
  activeFormatField,
  setActiveFormatField,
  addDropdownOption,
  removeDropdownOption,
}) {
  if (loadingTemplateData) {
    return (
      <Flex justify="center" align="center" style={{ padding: "60px 0" }}>
        <Spin tip="Loading template..." />
      </Flex>
    );
  }

  if (!activeTemplate) {
    return (
      <div style={{ textAlign: "center", padding: "60px 20px", color: "#94a3b8" }}>
        <FileTextOutlined style={{ fontSize: 36, marginBottom: 12, color: "#cbd5e1" }} />
        <Text style={{ display: "block", color: "#64748b" }}>Select a proposal template above to start customizing.</Text>
      </div>
    );
  }

  const isFieldVisible = (field) => {
    if (displayMode === "all") return true;
    return field.pageIndexes?.includes(activePageIndex);
  };

  const visibleStandard = standardPlaceholders.filter(isFieldVisible);
  const visibleCustom = customPlaceholders.filter(isFieldVisible);
  const visibleDropdown = dropdownPlaceholders.filter(isFieldVisible);
  const visibleTexts = editableTextLayers.filter((et) => displayMode === "all" || et.pageIndex === activePageIndex);
  const totalVisible = visibleStandard.length + visibleCustom.length + visibleDropdown.length + visibleTexts.length;

const updateItemValueByKey = (items, key, html) =>
  items.map((item) => (item.key === key ? { ...item, value: html } : item));

const updateItemValueByLayerId = (items, layerId, html) =>
  items.map((item) => (item.layerId === layerId ? { ...item, value: html } : item));

const handleUpdateCustomPlaceholder = (key, html) => {
  setCustomPlaceholders((prev) => updateItemValueByKey(prev, key, html));
};

const handleUpdateEditableText = (layerId, html) => {
  setEditableTextLayers((prev) => updateItemValueByLayerId(prev, layerId, html));
};

  const handleToggleFormatField = (fieldKey) => {
    setActiveFormatField((prev) => (prev === fieldKey ? null : fieldKey));
  };

  if (totalVisible === 0) {
    return (
      <Card style={{ textAlign: "center", padding: "28px 14px", background: "#f8fafc", borderRadius: 8, borderColor: "#e2e8f0", marginTop: 14 }}>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <div>
              <Text strong style={{ color: "#334155", display: "block", marginBottom: 4 }}>
                No dynamic fields on Page {activePageIndex + 1}
              </Text>
              <Text type="secondary" style={{ fontSize: "0.78rem" }}>
                Navigate pages below or switch to <b>All Pages</b> above to view all fields.
              </Text>
            </div>
          }
        />
      </Card>
    );
  }

  return (
    <Flex vertical gap={12} style={{ marginTop: 14 }}>
      <Flex justify="space-between" align="center">
        <Text strong style={{ fontSize: "0.82rem", color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px" }}>
          {displayMode === "all" ? `All Document Fields (${totalVisible})` : `Page ${activePageIndex + 1} Fields (${totalVisible})`}
        </Text>
        {displayMode === "single" && (
          <Tag color="blue" style={{ borderRadius: 6, margin: 0, fontWeight: 600 }}>
            Page {activePageIndex + 1} of {pagesCount}
          </Tag>
        )}
      </Flex>

      {/* 1. Account Details (Standard Lead Fields) */}
      {visibleStandard.map((sp) => (
        <div key={sp.key} className="pgm-field-card" style={{ padding: "10px 14px" }}>
          <Flex justify="space-between" align="center" style={{ marginBottom: 4 }}>
            <Text strong style={{ fontSize: "0.82rem", color: "#475569" }}>
              {sp.label}
            </Text>
            {displayMode === "all" && sp.pageIndexes && (
              <Tag style={{ fontSize: "0.68rem", margin: 0, borderRadius: 4 }}>
                P{sp.pageIndexes.map((p) => p + 1).join(", P")}
              </Tag>
            )}
          </Flex>
          <Input
            value={sp.value}
            maxLength={sp.maxChars}
            onChange={(e) => {
              sp.value = e.target.value;
              setStandardPlaceholders([...standardPlaceholders]);
            }}
            style={{ borderRadius: 6 }}
          />
        </div>
      ))}

      {/* 2. Custom Placeholders */}
      {visibleCustom.map((cp) => (
        <RichTextEditableField
          key={cp.key}
          item={cp}
          isToolbarOpen={activeFormatField === cp.key}
          onToggleToolbar={() => handleToggleFormatField(cp.key)}
          onUpdate={(html) => handleUpdateCustomPlaceholder(cp.key, html)}
          displayMode={displayMode}
        />
      ))}

      {/* 3. Dropdown Options */}
      {visibleDropdown.map((dp) => (
        <div key={dp.key} className="pgm-field-card">
          <Flex justify="space-between" align="center" style={{ marginBottom: 6 }}>
            <Text strong style={{ fontSize: "0.88rem" }}>
              {dp.label}
            </Text>
            {displayMode === "all" && dp.pageIndexes && (
              <Tag color="blue" style={{ fontSize: "0.68rem", margin: 0, borderRadius: 4 }}>
                P{dp.pageIndexes.map((p) => p + 1).join(", P")}
              </Tag>
            )}
          </Flex>
          <Flex wrap="wrap" gap={6} style={{ marginBottom: 10 }}>
            {dp.selectedOptions.map((opt, oIdx) => (
              <Tag key={`opt-${opt}-${oIdx}`} color="blue" closable onClose={() => removeDropdownOption(dp, oIdx)}>
                {opt}
              </Tag>
            ))}
          </Flex>
          <Flex gap={8}>
            <Select
              placeholder="Pick predefined..."
              style={{ flex: 1 }}
              options={(dp.options || []).filter((opt) => !dp.selectedOptions.includes(opt)).map((opt) => ({ label: opt, value: opt }))}
              onChange={(val) => addDropdownOption(dp, val)}
              value={undefined}
            />
            <Input
              placeholder="Or type custom..."
              style={{ flex: 1 }}
              value={dp.customInput}
              onChange={(e) => {
                dp.customInput = e.target.value;
                setDropdownPlaceholders([...dropdownPlaceholders]);
              }}
              onPressEnter={() => addDropdownOption(dp, dp.customInput)}
            />
            <Button icon={<PlusOutlined />} onClick={() => addDropdownOption(dp, dp.customInput)}>
              Add
            </Button>
          </Flex>
        </div>
      ))}

      {/* 4. Text Blocks (Rich HTML & Word Selection) */}
      {visibleTexts.map((et) => (
        <RichTextEditableField
          key={et.layerId}
          item={et}
          isToolbarOpen={activeFormatField === et.layerId}
          onToggleToolbar={() => handleToggleFormatField(et.layerId)}
          onUpdate={(html) => handleUpdateEditableText(et.layerId, html)}
          displayMode={displayMode}
        />
      ))}
    </Flex>
  );
}

const processPlaceholderLayer = (layer, pi, collections, helpers) => {
  const { custom, standard, dropdown } = collections;
  const { isStandardField, getStandardFieldValue } = helpers;
  const key = layer.placeholderLabel;
  if (!key) return;

  const existing = custom.find((c) => c.key === key) || standard.find((s) => s.key === key) || dropdown.find((d) => d.key === key);
  if (existing) {
    if (!existing.pageIndexes.includes(pi)) {
      existing.pageIndexes.push(pi);
    }
    return;
  }

  const maxChars = layer.placeholderMaxChars;
  const isDropdown = Array.isArray(layer.placeholderDropdownOptions) && layer.placeholderDropdownOptions.length > 0;

  if (isDropdown) {
    dropdown.push({
      key,
      label: key,
      pageIndexes: [pi],
      options: layer.placeholderDropdownOptions,
      selectedOptions: [],
      customInput: "",
      value: "[]",
    });
    return;
  }

  if (isStandardField(key)) {
    standard.push({
      key,
      label: key,
      pageIndexes: [pi],
      value: getStandardFieldValue(key),
      maxChars,
    });
    return;
  }

  const rawVal = layer.content || "";
  const initHtml = sanitizeProposalContent(rawVal === "Click to edit text" ? "" : rawVal);
  const fontFamily = layer.fontFamily && !layer.fontFamily.toLowerCase().includes("times") ? layer.fontFamily : "Arial";

  custom.push({
    key,
    label: key,
    pageIndexes: [pi],
    value: initHtml,
    _initialValue: initHtml,
    maxChars,
    fontSize: layer.fontSize || 16,
    fontFamily,
    color: layer.color || "#000000",
    bold: Boolean(layer.bold),
    italic: Boolean(layer.italic),
    align: layer.align || "left",
  });
};

const processEditableTextLayer = (layer, pi, li, editableTexts) => {
  if (layer.type !== "text" || layer.hidden || !layer.content || layer.content.trim() === "") {
    return;
  }
  const cleanText = htmlToCleanText(layer.content);
  if (!cleanText || cleanText === "Click to edit text") {
    return;
  }

  const firstLine = cleanText.split("\n")[0].trim();
  const shortLabel = firstLine.length > 32 ? `${firstLine.slice(0, 32)}...` : firstLine;
  const fontFamily = layer.fontFamily && !layer.fontFamily.toLowerCase().includes("times") ? layer.fontFamily : "Arial";
  const sanitized = sanitizeProposalContent(layer.content);

  editableTexts.push({
    layerId: layer.id || `layer_${pi}_${li}`,
    pageIndex: pi,
    pageIndexes: [pi],
    label: layer.layerName || shortLabel || `Text Block (Page ${pi + 1})`,
    value: sanitized,
    _initialValue: sanitized,
    fontSize: layer.fontSize || 16,
    fontFamily,
    color: layer.color || "#000000",
    bold: Boolean(layer.bold),
    italic: Boolean(layer.italic),
    align: layer.align || "left",
  });
};

const extractTemplateFields = (pages, helpers) => {
  const safePages = pages || [];
  const custom = [];
  const standard = [];
  const dropdown = [];
  const editableTexts = [];
  const collections = { custom, standard, dropdown };

  safePages.forEach((page, pi) => {
    (page.layers || []).forEach((layer, li) => {
      if (layer.type === "text" && layer.isPlaceholder) {
        processPlaceholderLayer(layer, pi, collections, helpers);
      } else {
        processEditableTextLayer(layer, pi, li, editableTexts);
      }
    });
  });

  return { custom, standard, dropdown, editableTexts };
};

const STANDARD_FIELD_RESOLVERS = {
  companyname: (lead) => lead.leadCompanyName || lead.companyName || "",
  leadcompanyname: (lead) => lead.leadCompanyName || lead.companyName || "",
  clientname: (lead) => lead.leadCompanyName || lead.companyName || "",
  contactname: (lead) => lead.contactName || (lead.directorFirstName ? `${lead.directorFirstName} ${lead.directorLastName || ""}`.trim() : ""),
  directorname: (lead) => lead.contactName || (lead.directorFirstName ? `${lead.directorFirstName} ${lead.directorLastName || ""}`.trim() : ""),
  clientcontact: (lead) => lead.contactName || (lead.directorFirstName ? `${lead.directorFirstName} ${lead.directorLastName || ""}`.trim() : ""),
  phone: (lead) => lead.contactNumber || lead.phone || "",
  contactnumber: (lead) => lead.contactNumber || lead.phone || "",
  mobile: (lead) => lead.contactNumber || lead.phone || "",
  email: (lead) => lead.directorEmailAddress || lead.companyEmail || lead.email || "",
  companyemail: (lead) => lead.directorEmailAddress || lead.companyEmail || lead.email || "",
  directoremailaddress: (lead) => lead.directorEmailAddress || lead.companyEmail || lead.email || "",
  proposaldate: () => new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }),
  date: () => new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }),
};

const resolveLeadFieldValue = (activeLead, fieldName) => {
  if (!activeLead || !fieldName) return "";
  const lower = fieldName.toLowerCase().replace(/[\s_-]/g, "");
  const resolver = STANDARD_FIELD_RESOLVERS[lower];
  if (resolver) {
    return resolver(activeLead);
  }
  const matchKey = Object.keys(activeLead).find((k) => k.toLowerCase() === lower);
  return matchKey ? String(activeLead[matchKey] || "") : "";
};

export default function ProposalGeneratorModal({
  visible,
  open,
  onClose,
  lead,
  prefillLead,
  initialLead,
  record,
  savedProposalRecord,
  companyCode,
  collaboratingCompanies = [],
  onProposalSaved,
  onProposalSent,
  autoOpenMail = false,
}) {
  const isModalOpen = Boolean(visible || open);
  const activeLead = lead || prefillLead || initialLead || null;
  const activeRecord = record || savedProposalRecord || null;
  const { companyCode: sessionCompanyCode } = useAuthSession();
  const initialCompany = companyCode || sessionCompanyCode;

  const [activeCompanyCode, setActiveCompanyCode] = useState(initialCompany);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [activeTemplate, setActiveTemplate] = useState(null);

  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [loadingTemplateData, setLoadingTemplateData] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [savingRecord, setSavingRecord] = useState(false);
  const [mailDrawerOpen, setMailDrawerOpen] = useState(false);
  const [mailPrefillData, setMailPrefillData] = useState(null);

  // Field states
  const [customPlaceholders, setCustomPlaceholders] = useState([]);
  const [standardPlaceholders, setStandardPlaceholders] = useState([]);
  const [dropdownPlaceholders, setDropdownPlaceholders] = useState([]);
  const [editableTextLayers, setEditableTextLayers] = useState([]);

  // Document Viewer states
  const [activePageIndex, setActivePageIndex] = useState(0);
  const [displayMode, setDisplayMode] = useState("single"); // "single" | "all"
  const [previewZoom, setPreviewZoom] = useState(0.68);
  const [activeFormatField, setActiveFormatField] = useState(null);

  const canvasScrollRef = useRef(null);
  const initialOpenRef = useRef(false);

  // Synchronize company code from caller / Redux
  useEffect(() => {
    if (initialCompany) {
      setActiveCompanyCode(initialCompany);
    }
  }, [initialCompany]);

  // Lead Data Helper for Dynamic Auto-fill
  const getStandardFieldValue = useCallback(
    (key) => {
      if (!activeLead) return "";
      const lower = key.toLowerCase();
      const matchKey = Object.keys(activeLead).find((k) => k.toLowerCase() === lower);
      return matchKey ? String(activeLead[matchKey] || "") : "";
    },
    [activeLead],
  );

  const isStandardField = useCallback((key) => {
    const lower = key.toLowerCase();
    return STANDARD_LEAD_FIELDS.some((f) => f.toLowerCase() === lower);
  }, []);

  // 1. Fetch template names list for a given company
  const fetchTemplatesForCompany = useCallback(async (compCode) => {
    if (!compCode) return;
    setLoadingTemplates(true);
    try {
      const res = await getProposalTemplates(compCode);
      if (res?.success) {
        const list = res.templates || [];
        setTemplates(list);
        if (activeRecord?.templateId) {
          setSelectedTemplateId(activeRecord.templateId);
        } else if (list.length > 0 && !selectedTemplateId) {
          setSelectedTemplateId(list[0]._id);
        }
      }
    } catch (err) {
      console.error("Error loading proposal templates:", err);
      message.error("Failed to load proposal templates.");
    } finally {
      setLoadingTemplates(false);
    }
  }, [activeRecord]);

  useEffect(() => {
    if (isModalOpen && !initialOpenRef.current) {
      initialOpenRef.current = true;
      const initialComp = companyCode || sessionCompanyCode || "";
      setActiveCompanyCode(initialComp);
      fetchTemplatesForCompany(initialComp);
    } else if (!isModalOpen) {
      initialOpenRef.current = false;
      setSelectedTemplateId("");
      setActiveTemplate(null);
      setCustomPlaceholders([]);
      setStandardPlaceholders([]);
      setDropdownPlaceholders([]);
      setEditableTextLayers([]);
      setActivePageIndex(0);
    }
  }, [isModalOpen, companyCode, sessionCompanyCode, fetchTemplatesForCompany]);

  // 2. Load Single Selected Template alone
  useEffect(() => {
    if (!selectedTemplateId) {
      setActiveTemplate(null);
      return;
    }

    let isMounted = true;
    async function loadTemplate() {
      setLoadingTemplateData(true);
      try {
        const res = await getProposalTemplateById(activeCompanyCode, selectedTemplateId);
        if (!isMounted) return;

        const tpl = res?.template || templates.find((t) => t._id === selectedTemplateId);
        if (!tpl?.pages) return;

        setActiveTemplate(tpl);
        setActivePageIndex(0);

        const { custom, standard, dropdown, editableTexts } = extractTemplateFields(tpl.pages, {
          isStandardField,
          getStandardFieldValue,
        });

        if (activeRecord?.valuesSnapshot) {
          const snap = activeRecord.valuesSnapshot;
          custom.forEach((c) => {
            if (snap[c.key] !== undefined) c.value = snap[c.key];
          });
          standard.forEach((s) => {
            if (snap[s.key] !== undefined) s.value = snap[s.key];
          });
          dropdown.forEach((d) => {
            if (snap[d.key] !== undefined) d.value = snap[d.key];
          });
        }

        setCustomPlaceholders(custom);
        setStandardPlaceholders(standard);
        setDropdownPlaceholders(dropdown);
        setEditableTextLayers(editableTexts);
      } catch (err) {
        console.error("Error loading template details:", err);
        message.error("Failed to load template data.");
      } finally {
        if (isMounted) setLoadingTemplateData(false);
      }
    }

    loadTemplate();
    return () => {
      isMounted = false;
    };
  }, [selectedTemplateId, activeCompanyCode, templates, isStandardField, getStandardFieldValue, activeRecord]);

  // Build current dynamicData dictionary
  const getDynamicData = () => {
    const dict = {};
    standardPlaceholders.forEach((sp) => {
      dict[sp.key] = sp.value;
    });
    customPlaceholders.forEach((cp) => {
      dict[cp.key] = cp.value;
    });
    dropdownPlaceholders.forEach((dp) => {
      dict[dp.key] = dp.value;
    });
    return dict;
  };

  // Dropdown Pill Handlers
  const addDropdownOption = (dp, option) => {
    const trimmed = (option || "").trim();
    if (trimmed && !dp.selectedOptions.includes(trimmed)) {
      const nextOptions = [...dp.selectedOptions, trimmed];
      dp.selectedOptions = nextOptions;
      dp.value = JSON.stringify(nextOptions);
      setDropdownPlaceholders([...dropdownPlaceholders]);
    }
    dp.customInput = "";
  };

  const removeDropdownOption = (dp, index) => {
    const nextOptions = [...dp.selectedOptions];
    nextOptions.splice(index, 1);
    dp.selectedOptions = nextOptions;
    dp.value = JSON.stringify(nextOptions);
    setDropdownPlaceholders([...dropdownPlaceholders]);
  };

  // Generate PDF Blob using pdf-lib
  const buildPdfBlob = async () => {
    if (!activeTemplate?.pages) return null;
    const doc = await PDFDocument.create();
    const dynamicData = getDynamicData();
    const context = { dynamicData, editableTextLayers, customPlaceholders };

    for (let pi = 0; pi < activeTemplate.pages.length; pi++) {
      const page = activeTemplate.pages[pi];
      const pdfPage = await renderPdfBackgroundPage(doc, page);
      await renderPdfPageOverlays(doc, pdfPage, page, pi, context);
    }

    const pdfBytes = await doc.save();
    return new Blob([pdfBytes], { type: "application/pdf" });
  };

  // Save Proposal to Database Record
  const saveProposalRecord = async (extraStatus = "generated") => {
    if (!activeTemplate) return null;
    try {
      const dynamicData = getDynamicData();
      const companyName = activeLead?.leadCompanyName || activeLead?.companyName || "Client Account";
      const title = `${activeTemplate.name} - ${companyName}`;

      const payload = {
        companyCode: activeCompanyCode,
        brandingCompanyCode: activeCompanyCode,
        templateId: activeTemplate._id,
        templateName: activeTemplate.name,
        title,
        leadId: activeLead?._id && typeof activeLead._id === "string" && activeLead._id.length === 24 ? activeLead._id : undefined,
        clientId: activeLead?.clientId || "",
        leadCompanyName: companyName,
        contactName: activeLead?.contactName || activeLead?.primaryContact || "",
        contactNumber: activeLead?.contactNumber || activeLead?.phone || "",
        directorEmailAddress: activeLead?.directorEmailAddress || activeLead?.email || "",
        valuesSnapshot: dynamicData,
        status: extraStatus,
      };

      const res = await createProposal(activeCompanyCode, payload);
      if (res?.success !== false && (res?.proposal || res?.data?.proposal)) {
        return res.proposal || res.data?.proposal;
      }
      return null;
    } catch (err) {
      console.error("Save proposal error:", err);
      return null;
    }
  };

  // Download PDF Action
  const handleDownloadPdf = async () => {
    setGenerating(true);
    try {
      const blob = await buildPdfBlob();
      if (!blob) throw new Error("Failed to generate PDF");

      // Auto-save proposal to DB and get generated Proposal Number
      const savedProp = await saveProposalRecord("generated");
      const propNum = savedProp?.proposalNumber || "PROP";

      const companyName = activeLead?.leadCompanyName || activeLead?.companyName || "Proposal";
      const cleanName = `${propNum}_${companyName.replace(/[^a-zA-Z0-9]/g, "_")}_${activeTemplate.name || "Proposal"}.pdf`;

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = cleanName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

      if (savedProp?.proposalNumber) {
        message.success(`Proposal #${savedProp.proposalNumber} created, saved, and downloaded!`);
      } else {
        message.success("PDF Proposal downloaded successfully!");
      }

      if (savedProp && onProposalSaved) {
        onProposalSaved(savedProp);
      }
      if (onProposalSent) {
        onProposalSent({ action: "download", proposal: savedProp, templateName: activeTemplate.name });
      }
    } catch (err) {
      console.error("PDF Download error:", err);
      message.error("Failed to generate PDF document.");
    } finally {
      setGenerating(false);
    }
  };

  const prepareAndOpenMailComposerWithProposal = async (savedProp) => {
    setGenerating(true);
    try {
      const blob = await buildPdfBlob();
      if (!blob) throw new Error("Failed to generate PDF");

      const prop = savedProp || (await saveProposalRecord("generated"));
      const propNum = prop?.proposalNumber || "PROP";
      const customerCompanyName = activeLead?.leadCompanyName || activeLead?.companyName || activeLead?.contactName || "Customer";
      const cleanCustomerName = customerCompanyName
        .replace(/[^a-zA-Z0-9]/g, "_")
        .replace(/_+/g, "_")
        .replace(/^_|_$/g, "");
      const fileName = `${cleanCustomerName}_${propNum}.pdf`;

      const proposalPdfFile = new File([blob], fileName, { type: "application/pdf" });

      const emailSubject = `Business Proposal ${propNum} - ${customerCompanyName}`;
      const emailBody = `<p>Dear ${activeLead?.contactName || "Sir/Madam"},</p>
<p>Thank you for the opportunity to present our services. Please find attached our detailed business proposal <strong>${activeTemplate?.name || "Proposal"}</strong> for <strong>${customerCompanyName}</strong>.</p>
<p><strong>Proposal Reference:</strong> ${propNum}</p>
<p>Kindly review the attached PDF document and let us know if you have any questions or require any adjustments.</p>
<br/>
<p>Warm regards,<br/><strong>Sales Team</strong><br/>${activeCompanyCode}</p>`;

      setMailPrefillData({
        to: activeLead?.directorEmailAddress || activeLead?.email || "",
        subject: emailSubject,
        html: emailBody,
        attachments: [proposalPdfFile],
      });
      setMailDrawerOpen(true);
    } catch (err) {
      console.error("Send proposal email error:", err);
      message.error("Failed to generate proposal PDF for email.");
    } finally {
      setGenerating(false);
    }
  };

  // Save Proposal to Database Record
  const handleSaveProposal = async () => {
    if (!activeTemplate) return;
    setSavingRecord(true);
    try {
      const savedProp = await saveProposalRecord("generated");
      if (savedProp) {
        message.success(`Proposal #${savedProp.proposalNumber || "Created"} saved successfully!`);
        if (onProposalSaved) onProposalSaved(savedProp);
        await prepareAndOpenMailComposerWithProposal(savedProp);
      } else {
        message.error("Failed to save proposal record.");
      }
    } catch (err) {
      console.error("Save proposal error:", err);
      message.error("Failed to save proposal record.");
    } finally {
      setSavingRecord(false);
    }
  };

  // Render a full crisp A4 page
  const renderA4Sheet = (page, pageIndex) => (
    <PreviewA4Sheet
      key={page.id || pageIndex}
      page={page}
      pageIndex={pageIndex}
      totalPages={activeTemplate.pages.length}
      previewZoom={previewZoom}
      dynamicData={getDynamicData()}
      editableTextLayers={editableTextLayers}
      customPlaceholders={customPlaceholders}
    />
  );

  const pagesCount = activeTemplate?.pages?.length || 0;

  return (
    <Modal open={isModalOpen} onCancel={onClose} closable={false} footer={null} width={1280} centered className="pgm-modal" destroyOnClose>
      {/* ── HEADER ── */}
      <div className="pgm-header">
        <Flex align="center" gap={12}>
          <div style={{ width: 36, height: 36, borderRadius: 8, background: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <FileTextOutlined style={{ color: "#2563eb", fontSize: 18 }} />
          </div>
          <Flex vertical gap={1}>
            <span className="pgm-kicker">Proposal Builder</span>
            <Flex align="center" gap={8}>
              <Title level={4} style={{ margin: 0, fontWeight: 700, color: "#0f172a" }}>
                Generate Proposal
              </Title>
              {activeLead && (
                <Tag color="blue" style={{ borderRadius: 6, fontWeight: 600 }}>
                  {activeLead.leadCompanyName || activeLead.companyName || "Target Account"}
                </Tag>
              )}
            </Flex>
          </Flex>
        </Flex>

        <Flex align="center" gap={10}>
          {collaboratingCompanies.length > 0 && (
            <Select
              size="small"
              value={activeCompanyCode}
              onChange={(val) => {
                setActiveCompanyCode(val);
                setSelectedTemplateId("");
                setActiveTemplate(null);
                fetchTemplatesForCompany(val);
              }}
              style={{ width: 140 }}
              options={[{ label: `${companyCode} (Main)`, value: companyCode }, ...collaboratingCompanies.map((c) => ({ label: c, value: c }))]}
            />
          )}

          {activeTemplate && (
            <Space size={8}>
              <Button
                icon={<MailOutlined />}
                loading={generating}
                onClick={() => prepareAndOpenMailComposerWithProposal()}
                style={{ borderRadius: 8 }}
              >
                Send Email
              </Button>
              <Button type="primary" icon={<DownloadOutlined />} loading={generating} onClick={handleDownloadPdf} style={{ borderRadius: 8, backgroundColor: "#2563eb" }}>
                Download PDF
              </Button>
              <Button type="primary" icon={<SaveOutlined />} loading={savingRecord} onClick={handleSaveProposal} style={{ borderRadius: 8, backgroundColor: "#4f46e5" }}>
                Save Proposal
              </Button>
            </Space>
          )}

          <Button type="text" icon={<CloseOutlined />} onClick={onClose} style={{ borderRadius: 8 }} />
        </Flex>
      </div>

      {/* ── WORKSPACE BODY ── */}
      <div className="pgm-workspace">
        {/* ── LEFT PANE: FORM CONTROLS ── */}
        <div className="pgm-left-pane">
          <div className="pgm-left-scroll">
            {/* 1. Template Selector */}
            <Card bordered={false} styles={{ body: { padding: 14 } }} style={{ background: "#f8fafc", borderRadius: 10, border: "1px solid #e2e8f0" }}>
              <Text strong style={{ fontSize: "0.82rem", color: "#475569", display: "block", marginBottom: 6 }}>
                SELECT PROPOSAL TEMPLATE
              </Text>
              <Select
                size="large"
                style={{ width: "100%" }}
                placeholder="Choose a proposal template..."
                value={selectedTemplateId || undefined}
                onChange={(val) => setSelectedTemplateId(val)}
                loading={loadingTemplates}
                options={templates.map((t) => ({
                  label: (
                    <Flex justify="space-between" align="center">
                      <span style={{ fontWeight: 500 }}>{t.name}</span>
                      <Text type="secondary" style={{ fontSize: "0.75rem" }}>
                        {t.pageCount || t.pages?.length || 1} page(s)
                      </Text>
                    </Flex>
                  ),
                  value: t._id,
                }))}
              />
            </Card>

            <ProposalSidebarFields
              activeTemplate={activeTemplate}
              loadingTemplateData={loadingTemplateData}
              displayMode={displayMode}
              activePageIndex={activePageIndex}
              pagesCount={pagesCount}
              standardPlaceholders={standardPlaceholders}
              setStandardPlaceholders={setStandardPlaceholders}
              customPlaceholders={customPlaceholders}
              setCustomPlaceholders={setCustomPlaceholders}
              dropdownPlaceholders={dropdownPlaceholders}
              setDropdownPlaceholders={setDropdownPlaceholders}
              editableTextLayers={editableTextLayers}
              setEditableTextLayers={setEditableTextLayers}
              activeFormatField={activeFormatField}
              setActiveFormatField={setActiveFormatField}
              addDropdownOption={addDropdownOption}
              removeDropdownOption={removeDropdownOption}
            />
          </div>

          {/* Left Footer Action */}
          {activeTemplate && (
            <div className="pgm-left-footer">
              <Text type="secondary" style={{ fontSize: "0.78rem" }}>
                {pagesCount} Page Document
              </Text>
              <Space>
                <Button type="primary" icon={<DownloadOutlined />} loading={generating} onClick={handleDownloadPdf} style={{ borderRadius: 6, backgroundColor: "#2563eb" }}>
                  Download PDF
                </Button>
                <Button type="primary" icon={<SaveOutlined />} loading={savingRecord} onClick={handleSaveProposal} style={{ borderRadius: 6, backgroundColor: "#4f46e5" }}>
                  Save
                </Button>
              </Space>
            </div>
          )}
        </div>

        {/* ── RIGHT PANE: DOCUMENT VIEWER ── */}
        <div className="pgm-preview-pane">
          {/* Top Viewer Controls */}
          {activeTemplate && (
            <div className="pgm-viewer-topbar">
              {/* Page Navigator */}
              <Flex align="center" gap={8}>
                <Button size="small" icon={<LeftOutlined />} disabled={activePageIndex <= 0} onClick={() => setActivePageIndex(activePageIndex - 1)} />
                <Text strong style={{ fontSize: "0.85rem", minWidth: 90, textAlign: "center" }}>
                  Page {activePageIndex + 1} of {pagesCount}
                </Text>
                <Button size="small" icon={<RightOutlined />} disabled={activePageIndex >= pagesCount - 1} onClick={() => setActivePageIndex(activePageIndex + 1)} />
              </Flex>

              {/* View Mode & Zoom Controls */}
              <Flex align="center" gap={12}>
                <Segmented
                  size="small"
                  value={displayMode}
                  onChange={setDisplayMode}
                  options={[
                    { label: "Single Page", value: "single" },
                    { label: "All Pages", value: "all" },
                  ]}
                />

                <Space.Compact size="small">
                  <Tooltip title="Zoom Out">
                    <Button icon={<ZoomOutOutlined />} onClick={() => setPreviewZoom(Math.max(0.45, previewZoom - 0.08))} />
                  </Tooltip>
                  <Button style={{ minWidth: 50, fontWeight: 600 }} onClick={() => setPreviewZoom(0.68)}>
                    {Math.round(previewZoom * 100)}%
                  </Button>
                  <Tooltip title="Zoom In">
                    <Button icon={<ZoomInOutlined />} onClick={() => setPreviewZoom(Math.min(1.15, previewZoom + 0.08))} />
                  </Tooltip>
                </Space.Compact>

                <Tooltip title="Reset Fit">
                  <Button size="small" icon={<CompressOutlined />} onClick={() => setPreviewZoom(0.68)}>
                    Fit
                  </Button>
                </Tooltip>
              </Flex>
            </div>
          )}

          {/* Canvas Scroll Area */}
          <div className="pgm-canvas-scroll" ref={canvasScrollRef}>
            {(() => {
              if (!activeTemplate) {
                return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Select a proposal template to preview the pages" style={{ marginTop: 160 }} />;
              }
              if (displayMode === "single") {
                return activeTemplate.pages[activePageIndex] ? renderA4Sheet(activeTemplate.pages[activePageIndex], activePageIndex) : null;
              }
              return activeTemplate.pages.map((page, pIdx) => renderA4Sheet(page, pIdx));
            })()}
          </div>

          {/* Bottom Thumbnails Strip for Quick Page Jumping */}
          {activeTemplate && pagesCount > 1 && (
            <div className="pgm-thumb-strip">
              {activeTemplate.pages.map((p, idx) => (
                <button key={`page-thumb-${p.id || idx}`} type="button" className={`pgm-thumb-btn ${idx === activePageIndex ? "active" : ""}`} onClick={() => setActivePageIndex(idx)}>
                  P{idx + 1}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Mail Compose Drawer with Generated Proposal Attachment */}
      <MailComposeDrawer
        open={mailDrawerOpen}
        visible={mailDrawerOpen}
        onClose={() => {
          setMailDrawerOpen(false);
          setMailPrefillData(null);
        }}
        lead={activeLead}
        prefillData={mailPrefillData}
        companyCode={activeCompanyCode}
        employeeInfo={{ companyName: activeCompanyCode }}
        onSuccess={() => {
          message.success("Proposal sent via email!");
          if (onProposalSent) onProposalSent();
        }}
      />
    </Modal>
  );
}
